import React, { useState, useEffect, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useLoading } from '../contexts/LoadingContext';
import { useError } from '../contexts/ErrorContext';
import { StaffFormModal, Staff, BankItem } from '../components/StaffFormModal';
import { useAuth } from '../contexts/AuthContext';
import { PERMISSIONS } from '../utils/permissions';

interface Schedule {
  id: string;
  staffId: string;
  workDate: string;
  shift: string;
  staffName: string;
  staffRole: string;
  staffColor: string;
  staffTextColor: string;
  isDeleted?: boolean;
}

interface WeekInfo {
  startDate: string;
  endDate: string;
  weekNumber: number;
}

const ROLE_OPTIONS = [
  { value: 'all', label: 'Tất cả vai trò' },
  { value: 'CSKH', label: 'Chăm sóc khách hàng' },
  { value: 'SX_AO', label: 'Sản xuất áo' },
  { value: 'SX_THE', label: 'Sản xuất thẻ' },
];

const SHIFTS = ['Sáng', 'Chiều', 'Tối'];
const DAYS = ['Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy', 'Chủ Nhật'];

interface Holiday {
  id?: string;
  holiday_date: string;
  name: string;
  multiplier: number;
}

export const SchedulePage: React.FC = () => {
  const { setLoading } = useLoading();
  const { error, setError, clearError } = useError();
  const { startDate, endDate } = useParams<{ startDate?: string; endDate?: string }>();
  const navigate = useNavigate();
  const { can } = useAuth();
  // Thêm/sửa hồ sơ nhân viên và hệ số ngày lễ là quyền riêng, không thuộc quyền quản lý lịch.
  const canManageEmployees = can(PERMISSIONS.employees);
  const canManageHolidays = can(PERMISSIONS.holidays);
  
  // State management
  const [activeTab, setActiveTab] = useState<'schedule' | 'stats'>('schedule');
  const [staff, setStaff] = useState<Staff[]>([]);
  const [schedules, setSchedules] = useState<Schedule[]>([]);
  const [currentWeek, setCurrentWeek] = useState<WeekInfo>(() => {
    // Initialize from URL params or default week
    if (startDate && endDate) {
      return {
        startDate,
        endDate,
        weekNumber: getWeekNumberFromDate(startDate)
      };
    }
    return getCurrentWeek();
  });
  const [selectedStaffIds, setSelectedStaffIds] = useState<Set<string>>(new Set());
  const [nameSearch, setNameSearch] = useState('');
  const [holidays, setHolidays] = useState<Holiday[]>([]);
  const [editingHolidayDate, setEditingHolidayDate] = useState<string | null>(null);
  const [editingHolidayMultiplier, setEditingHolidayMultiplier] = useState<number>(1.5);
  const [selectedRoles, setSelectedRoles] = useState<Set<string>>(new Set(['all']));
  const [hasChanges, setHasChanges] = useState(false);
  const [draggedStaff, setDraggedStaff] = useState<Staff | null>(null);
  const [showAddStaffModal, setShowAddStaffModal] = useState(false);
  const [showDeletedSchedules, setShowDeletedSchedules] = useState(true);
  const [staffStatusFilter, setStaffStatusFilter] = useState<'Hoạt động' | 'Đã nghỉ' | 'all'>('Hoạt động');
  const [banks, setBanks] = useState<BankItem[]>([]);
  
  // Statistics state
  const [statsStartDate, setStatsStartDate] = useState<string>(() => {
    const now = new Date();
    const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
    return formatDateForAPI(startOfMonth);
  });
  const [statsEndDate, setStatsEndDate] = useState<string>(() => {
    const now = new Date();
    return formatDateForAPI(now);
  });
  const [scheduleStats, setScheduleStats] = useState<{
    totalSchedules: number;
    uniqueStaff: number;
    schedulesByShift: Record<string, number>;
    schedulesByStaff: Array<{ staff_name: string; staff_role: string; count: number }>;
  } | null>(null);
  
  // Filter and sort state for staff stats table
  const [nameFilter, setNameFilter] = useState('');
  const [roleFilter, setRoleFilter] = useState('');
  const [sortColumn, setSortColumn] = useState<'staff_name' | 'staff_role' | 'count' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');
  
  // Batch operations state
  const [pendingChanges, setPendingChanges] = useState<{
    toAdd: Array<{ staffId: string; workDate: string; shift: string }>;
    toDelete: string[];
  }>({ toAdd: [], toDelete: [] });
  
  // Form states
  const [editingStaff, setEditingStaff] = useState<Staff | null>(null);
  
  // Refs
  const dragOverRef = useRef<HTMLTableCellElement | null>(null);
  
  // Prevent duplicate API calls
  const isLoadingRef = useRef<boolean>(false);

  // Helper functions
  function formatDateForAPI(date: Date): string {
    // Convert Date to YYYY-MM-DD format for API using local timezone
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  // Helper function to add days to a date string (YYYY-MM-DD) without timezone issues
  function addDaysToDateString(dateString: string, days: number): string {
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day + days);
    return formatDateForAPI(date);
  }

  function parseDateFromAPI(dateString: string): string {
    // Server now returns date as YYYY-MM-DD string, so just return it directly
    if (typeof dateString === 'string') {
      // If it's already in YYYY-MM-DD format, return as is
      if (/^\d{4}-\d{2}-\d{2}$/.test(dateString)) {
        return dateString;
      }
      // If it contains timestamp, extract just the date part (YYYY-MM-DD)
      if (dateString.includes('T')) {
        return dateString.split('T')[0];
      }
      // If it's in other format, try to extract YYYY-MM-DD
      const dateMatch = dateString.match(/(\d{4}-\d{2}-\d{2})/);
      if (dateMatch) {
        return dateMatch[1];
      }
      return dateString;
    }
    // If it's not a string, return as is (shouldn't happen)
    return String(dateString);
  }

  function getWeekNumberFromDate(dateString: string): number {
    // Parse date string YYYY-MM-DD directly
    const [year, month, day] = dateString.split('-').map(Number);
    const date = new Date(year, month - 1, day);
    const startOfYear = new Date(year, 0, 1);
    const weekNumber = Math.ceil((date.getTime() - startOfYear.getTime()) / (7 * 24 * 60 * 60 * 1000));
    return weekNumber;
  }

  function getCurrentWeek(): WeekInfo {
    const now = new Date();
    const startOfWeek = new Date(now);
    startOfWeek.setDate(now.getDate() - now.getDay() + 1); // Monday
    const endOfWeek = new Date(startOfWeek);
    endOfWeek.setDate(startOfWeek.getDate() + 6); // Sunday
    
    const startDateStr = formatDateForAPI(startOfWeek);
    const endDateStr = formatDateForAPI(endOfWeek);
    
    return {
      startDate: startDateStr,
      endDate: endDateStr,
      weekNumber: getWeekNumberFromDate(startDateStr)
    };
  }

  function getWeekRange(week: WeekInfo): string {
    // Parse date string YYYY-MM-DD directly without timezone conversion
    const parseDateString = (dateStr: string): string => {
      const [year, month, day] = dateStr.split('-');
      return `${day}/${month}/${year}`;
    };
    const start = parseDateString(week.startDate);
    const end = parseDateString(week.endDate);
    return `${start} - ${end}`;
  }

  // Helper function to get text color based on background color (for contrast)
  function getTextColor(hexColor: string): string {
    if (!hexColor) return '#ffffff';
    
    // Remove # if present
    const hex = hexColor.replace('#', '');
    
    // Convert to RGB
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);
    
    // Calculate luminance
    const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    
    // Return black or white based on luminance
    return luminance > 0.5 ? '#333333' : '#ffffff';
  }

  // Get color info for staff (uses color from database or default)
  function getStaffColorInfo(staffColor?: string) {
    const defaultColor = '#3498db'; // Default blue color
    const bgColor = staffColor || defaultColor;
    const textColor = getTextColor(bgColor);
    
    return { bg: bgColor, text: textColor };
  }

  // Sync with URL params
  useEffect(() => {
    if (startDate && endDate) {
      const newWeek = {
        startDate,
        endDate,
        weekNumber: getWeekNumberFromDate(startDate)
      };
      setCurrentWeek(newWeek);
    }
  }, [startDate, endDate]);

  // Load data
  useEffect(() => {
    let isMounted = true;
    
    const loadDataSafe = async () => {
      if (!isMounted) return;
      await loadData();
    };
    
    loadDataSafe();
    
    return () => {
      isMounted = false;
    };
  }, [currentWeek]);

  const loadHolidays = async () => {
    try {
      const response = await fetch('/api/holidays');
      const data = await response.json();
      if (data.success) {
        setHolidays(data.data);
      }
    } catch (err) {
      console.error('Error loading holidays:', err);
    }
  };

  const handleSaveHolidayMultiplier = async () => {
    if (!editingHolidayDate) return;
    
    try {
      setLoading(true, 'Đang lưu hệ số lương...');
      clearError();
      const existingHoliday = holidays.find(h => h.holiday_date === editingHolidayDate);
      
      if (editingHolidayMultiplier === 1.0) {
        if (existingHoliday && existingHoliday.id) {
          const response = await fetch(`/api/holidays/${existingHoliday.id}`, {
            method: 'DELETE'
          });
          const data = await response.json();
          if (!data.success) {
            throw new Error(data.error || 'Lỗi khi xóa ngày lễ');
          }
        }
      } else {
        if (existingHoliday && existingHoliday.id) {
          const response = await fetch(`/api/holidays/${existingHoliday.id}`, {
            method: 'PUT',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              holiday_date: editingHolidayDate,
              name: '',
              multiplier: editingHolidayMultiplier
            })
          });
          const data = await response.json();
          if (!data.success) {
            throw new Error(data.error || 'Lỗi khi cập nhật ngày lễ');
          }
        } else {
          const response = await fetch('/api/holidays', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              holiday_date: editingHolidayDate,
              name: '',
              multiplier: editingHolidayMultiplier
            })
          });
          const data = await response.json();
          if (!data.success) {
            throw new Error(data.error || 'Lỗi khi tạo ngày lễ');
          }
        }
      }
      
      setEditingHolidayDate(null);
      await loadHolidays();
    } catch (err: any) {
      console.error('Error saving holiday multiplier:', err);
      setError(err.message || 'Lỗi khi lưu hệ số lương');
    } finally {
      setLoading(false);
    }
  };

  const loadData = async () => {
    // Prevent duplicate calls
    if (isLoadingRef.current) {
      return;
    }
    
    try {
      isLoadingRef.current = true;
      setLoading(true, 'Đang tải dữ liệu...');
      clearError();
      
      // Load staff
      const staffResponse = await fetch('/api/staff?all=true');
      const staffData = await staffResponse.json();
      if (staffData.success) {
        // Use color from database, or default if not set
        const staffWithColors = staffData.data.map((s: any) => ({
          ...s,
          color: s.color || '#3498db' // Default blue if no color in DB
        }));
        setStaff(staffWithColors);
      }
      
      // Load schedules
      const scheduleResponse = await fetch(`/api/schedule/${currentWeek.startDate}/${currentWeek.endDate}`);
      const scheduleData = await scheduleResponse.json();
      
      if (scheduleData.success) {
        // Map server data to client format
        const mappedSchedules = scheduleData.data.map((s: any) => {
          // Use color from schedule data (staff_color from JOIN), or from staff list, or default
          const staffColor = s.staff_color || staff.find(st => st.id === s.staff_id)?.color || '#3498db';
          const colorInfo = getStaffColorInfo(staffColor);
          const parsedDate = parseDateFromAPI(s.work_date);
          const isDeleted = s.is_deleted || false;
          return {
            id: s.id,
            staffId: s.staff_id,
            workDate: parsedDate,
            shift: s.shift,
            staffName: s.staff_name,
            staffRole: s.staff_role,
            staffColor: isDeleted ? '#cccccc' : colorInfo.bg, // Gray for deleted
            staffTextColor: isDeleted ? '#666666' : colorInfo.text,
            isDeleted: isDeleted
          };
        });
        setSchedules(mappedSchedules);
      }
      
      // Load holidays
      await loadHolidays();

      // Load banks
      try {
        const banksResponse = await fetch('/api/banks');
        const banksData = await banksResponse.json();
        if (banksData.success) {
          setBanks(banksData.data || []);
        }
      } catch (bankErr) {
        console.error('Failed to load banks:', bankErr);
      }
      
      setLoading(false);
    } catch (err) {
      setError('Lỗi khi tải dữ liệu');
      setLoading(false);
    } finally {
      isLoadingRef.current = false;
    }
  };

  // Staff management
  const addNewStaff = async (staffData: Partial<Staff>) => {
    if (!staffData.name?.trim()) return;
    
    try {
      setLoading(true, 'Đang thêm nhân viên...');
      const response = await fetch('/api/staff', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(staffData)
      });
      
      const data = await response.json();
      if (data.success) {
        const newStaffWithColor: Staff = {
          ...data.data,
          color: data.data.color || '#8d2a2c'
        };
        setStaff(prev => [...prev, newStaffWithColor]);
        setShowAddStaffModal(false);
        setEditingStaff(null);
        showSuccess('Thêm nhân viên thành công');
      } else {
        setError(data.error || 'Lỗi khi thêm nhân viên');
        throw new Error(data.error || 'Lỗi khi thêm nhân viên');
      }
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Lỗi khi thêm nhân viên');
      setLoading(false);
      throw err;
    }
  };

  const updateStaff = async (staffId: string, updates: Partial<Staff>) => {
    try {
      setLoading(true, 'Đang cập nhật nhân viên...');
      
      const response = await fetch(`/api/staff/${staffId}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(updates)
      });
      
      const data = await response.json();
      if (data.success) {
        setStaff(prev => prev.map(s => s.id === staffId ? { ...s, ...data.data, ...updates } : s));
        setEditingStaff(null);
        setShowAddStaffModal(false);
        showSuccess('Cập nhật nhân viên thành công');
      } else {
        setError(data.error || 'Lỗi khi cập nhật nhân viên');
        throw new Error(data.error || 'Lỗi khi cập nhật nhân viên');
      }
      setLoading(false);
    } catch (err: any) {
      setError(err.message || 'Lỗi khi cập nhật nhân viên');
      setLoading(false);
      throw err;
    }
  };

  const handleSaveStaff = async (staffData: Partial<Staff>) => {
    if (editingStaff) {
      await updateStaff(editingStaff.id, staffData);
    } else {
      await addNewStaff(staffData);
    }
  };

  const deleteStaff = async (staffId: string) => {
    if (!confirm('Bạn có chắc chắn muốn xóa nhân viên này?')) return;
    
    try {
      setLoading(true, 'Đang xóa nhân viên...');
      const response = await fetch(`/api/staff/${staffId}`, {
        method: 'DELETE'
      });
      
      const data = await response.json();
      if (data.success) {
        setStaff(prev => prev.filter(s => s.id !== staffId));
        setSelectedStaffIds(prev => {
          const newSet = new Set(prev);
          newSet.delete(staffId);
          return newSet;
        });
        setEditingStaff(null);
        setShowAddStaffModal(false);
        showSuccess('Xóa nhân viên thành công');
      } else {
        setError(data.error || 'Lỗi khi xóa nhân viên');
      }
      setLoading(false);
    } catch (err) {
      setError('Lỗi khi xóa nhân viên');
      setLoading(false);
    }
  };

  // Schedule management - Add to pending changes
  const addScheduleToPending = (staffId: string, workDate: string, shift: string) => {
    // Check for duplicate in current schedules
    const existingSchedule = schedules.find(s => 
      s.staffId === staffId && s.workDate === workDate && s.shift === shift
    );
    
    if (existingSchedule) {
      setError('Nhân viên đã có ca làm việc trong khung giờ này');
      return;
    }

    // Check for duplicate in pending additions
    const pendingDuplicate = pendingChanges.toAdd.find(p => 
      p.staffId === staffId && p.workDate === workDate && p.shift === shift
    );
    
    if (pendingDuplicate) {
      setError('Ca làm việc này đã được thêm vào danh sách chờ');
      return;
    }

    // Add to pending changes
    setPendingChanges(prev => ({
      ...prev,
      toAdd: [...prev.toAdd, { staffId, workDate, shift }]
    }));

    // Update local state for immediate UI feedback
    const staffMember = staff.find(s => s.id === staffId);
    if (staffMember) {
      const colorInfo = getStaffColorInfo(staffMember.color);
      const tempId = `temp_${Date.now()}_${Math.random()}`;
      const newSchedule: Schedule = {
        id: tempId,
        staffId,
        workDate,
        shift,
        staffName: staffMember.name,
        staffRole: staffMember.role || '',
        staffColor: colorInfo.bg,
        staffTextColor: colorInfo.text
      };
      setSchedules(prev => [...prev, newSchedule]);
      setHasChanges(true);
    }
  };

  const removeScheduleFromPending = (scheduleId: string) => {
    // Check if it's a temporary schedule (pending addition)
    if (scheduleId.startsWith('temp_')) {
      // Remove from pending additions
      setPendingChanges(prev => ({
        ...prev,
        toAdd: prev.toAdd.filter((_, _index) => {
          const tempId = `temp_${Date.now()}_${Math.random()}`;
          return tempId !== scheduleId;
        })
      }));
    } else {
      // Add to pending deletions
      setPendingChanges(prev => ({
        ...prev,
        toDelete: [...prev.toDelete, scheduleId]
      }));
    }

    // Update local state for immediate UI feedback - mark as deleted instead of removing
    setSchedules(prev => prev.map(s => 
      s.id === scheduleId ? { ...s, isDeleted: true, staffColor: '#cccccc', staffTextColor: '#666666' } : s
    ));
    setHasChanges(true);
  };

  // Restore deleted schedule
  const restoreSchedule = async (scheduleId: string) => {
    try {
      setLoading(true, 'Đang khôi phục ca làm việc...');
      const response = await fetch(`/api/schedule/${scheduleId}/restore`, {
        method: 'POST'
      });
      
      const data = await response.json();
      if (data.success) {
        // Reload data to get updated schedules from server
        await loadData();
        showSuccess('Đã khôi phục ca làm việc thành công');
      } else {
        setError(data.error || 'Lỗi khi khôi phục ca làm việc');
      }
      setLoading(false);
    } catch (err) {
      setError('Lỗi khi khôi phục ca làm việc');
      setLoading(false);
    }
  };

  // Save all pending changes
  const savePendingChanges = async () => {
    if (pendingChanges.toAdd.length === 0 && pendingChanges.toDelete.length === 0) {
      setError('Không có thay đổi nào để lưu');
      return;
    }

    try {
      setLoading(true, 'Đang lưu thay đổi...');
      
      const token = localStorage.getItem('auth_token');
      const response = await fetch('/api/schedule/batch', {
        method: 'POST',
        headers: { 
          'Content-Type': 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {})
        },
        body: JSON.stringify(pendingChanges)
      });
      
      const data = await response.json();
      
      if (data.success) {
        // Clear pending changes
        setPendingChanges({ toAdd: [], toDelete: [] });
        setHasChanges(false);
        
        // Reload data to get updated schedules from server
        await loadData();
        
        showSuccess(`Đã lưu ${data.data.added.length} ca mới và xóa ${data.data.deleted} ca`);
      } else {
        setError(data.error || 'Lỗi khi lưu thay đổi');
      }
    } catch (err) {
      console.error('Error saving changes:', err);
      setError('Lỗi khi lưu thay đổi');
    } finally {
      setLoading(false);
    }
  };

  // Cancel pending changes
  const cancelPendingChanges = () => {
    setPendingChanges({ toAdd: [], toDelete: [] });
    setHasChanges(false);
    // Reload data to reset to server state
    loadData();
  };

  // Drag and drop
  const handleDragStart = (e: React.DragEvent, staff: Staff) => {
    setDraggedStaff(staff);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    dragOverRef.current = e.currentTarget as HTMLTableCellElement;
    e.currentTarget.classList.add('drag-over');
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.currentTarget.classList.remove('drag-over');
  };

  const handleDrop = (e: React.DragEvent, workDate: string, shift: string) => {
    e.preventDefault();
    e.currentTarget.classList.remove('drag-over');
    
    if (draggedStaff) {
      addScheduleToPending(draggedStaff.id, workDate, shift);
      setDraggedStaff(null);
    }
  };

  // Filter functions
  const filteredStaff = staff.filter(s => {
    // Status filter (defaults to 'Hoạt động')
    const statusNormalized = (s.status || '').trim();
    let statusMatch = true;
    if (staffStatusFilter === 'Hoạt động') {
      statusMatch = statusNormalized === 'Hoạt động';
    } else if (staffStatusFilter === 'Đã nghỉ') {
      statusMatch = statusNormalized === 'Đã nghỉ';
    }

    const roleMatch = selectedRoles.has('all') || (s.role ? selectedRoles.has(s.role) : false) || (selectedRoles.size === 0);
    const staffMatch = selectedStaffIds.size === 0 || selectedStaffIds.has(s.id);
    const nameMatch = nameSearch === '' || s.name.toLowerCase().includes(nameSearch.toLowerCase());
    return statusMatch && roleMatch && staffMatch && nameMatch;
  });

  // Handle role filter toggle
  const toggleRoleFilter = (roleValue: string) => {
    setSelectedRoles(prev => {
      const newSet = new Set(prev);
      if (roleValue === 'all') {
        // If "all" is clicked, toggle all roles
        if (newSet.has('all')) {
          newSet.clear();
        } else {
          newSet.clear();
          newSet.add('all');
        }
      } else {
        // Remove 'all' if a specific role is selected
        newSet.delete('all');
        if (newSet.has(roleValue)) {
          newSet.delete(roleValue);
        } else {
          newSet.add(roleValue);
        }
        // If no roles selected, select all
        if (newSet.size === 0) {
          newSet.add('all');
        }
      }
      return newSet;
    });
  };

  const getScheduleForSlot = (workDate: string, shift: string) => {
    const result = schedules.filter(s => {
      // Check date and shift match
      if (s.workDate !== workDate || s.shift !== shift) {
        return false;
      }
      
      // Check if schedule is deleted - if showDeletedSchedules is false, hide deleted ones
      if (!showDeletedSchedules && s.isDeleted) {
        return false;
      }
      
      const roleMatch = selectedRoles.has('all') || selectedRoles.has(s.staffRole) || (selectedRoles.size === 0);
      const staffMatch = selectedStaffIds.size === 0 || selectedStaffIds.has(s.staffId);
      const nameMatch = nameSearch === '' || (s.staffName || '').toLowerCase().includes(nameSearch.toLowerCase());
      return roleMatch && staffMatch && nameMatch;
    });
    
    return result;
  };

  // UI helpers
  const showSuccess = (message: string) => {
    // You can implement a success notification system here
    alert(message);
  };

  const handleWeekChange = (direction: 'prev' | 'next') => {
    // Add/subtract 7 days from current week start date
    const daysToAdd = direction === 'next' ? 7 : -7;
    const startDate = addDaysToDateString(currentWeek.startDate, daysToAdd);
    const endDate = addDaysToDateString(startDate, 6);
    
    // Navigate to new URL
    navigate(`/schedule/${startDate}/${endDate}`);
  };

  // Function to go to current week
  const goToCurrentWeek = () => {
    const currentWeek = getCurrentWeek();
    navigate(`/schedule/${currentWeek.startDate}/${currentWeek.endDate}`);
  };

  // Load statistics
  const loadStatistics = async () => {
    try {
      setLoading(true, 'Đang tải thống kê...');
      clearError();
      
      const response = await fetch(`/api/schedule/stats/${statsStartDate}/${statsEndDate}`);
      const data = await response.json();
      
      if (data.success) {
        setScheduleStats(data.data);
      } else {
        setError(data.error || 'Lỗi khi tải thống kê');
      }
      
      setLoading(false);
    } catch (err) {
      setError('Lỗi khi tải thống kê');
      setLoading(false);
    }
  };

  // Load statistics when tab changes to stats (only if no data exists)
  useEffect(() => {
    if (activeTab === 'stats' && !scheduleStats) {
      loadStatistics();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  return (
    <div className="schedule-page">
      <div className="container">
        {/* Top Header Synchronized with Bulletin Board */}
        <div className="schedule-header">
          <div>
            <h1 className="schedule-title">Quản Lý Lịch Làm Việc Part-time</h1>
            <p className="schedule-subtitle">
              Sắp xếp, theo dõi và phân công ca làm việc chi tiết theo tuần cho nhân sự các bộ phận
            </p>
          </div>

          <div className="schedule-header-actions">
            <div className="schedule-tabs">
              <button
                className={`schedule-tab-btn ${activeTab === 'schedule' ? 'active' : ''}`}
                onClick={() => setActiveTab('schedule')}
              >
                <i className="fas fa-calendar-week"></i>
                <span>Lịch làm việc</span>
              </button>
              <button
                className={`schedule-tab-btn ${activeTab === 'stats' ? 'active' : ''}`}
                onClick={() => setActiveTab('stats')}
              >
                <i className="fas fa-chart-bar"></i>
                <span>Thống kê</span>
              </button>
            </div>

            {canManageEmployees && (
              <button className="btn-primary-action" onClick={() => {
                setEditingStaff(null);
                setShowAddStaffModal(true);
              }}>
                <i className="fas fa-user-plus"></i>
                <span>Thêm nhân viên</span>
              </button>
            )}
          </div>
        </div>

        {/* Unified Schedule Toolbar (matching Bulletin Filter Bar) */}
        {activeTab === 'schedule' && (
          <div className="schedule-toolbar">
            <div className="toolbar-left">
              <div className="week-nav-group">
                <button 
                  className="toolbar-nav-btn" 
                  onClick={() => handleWeekChange('prev')}
                  title="Xem tuần trước"
                >
                  <i className="fas fa-chevron-left"></i>
                  <span>Tuần trước</span>
                </button>
                <div className="week-current-badge">
                  <i className="far fa-calendar-alt" style={{ color: 'var(--duni-primary)' }}></i>
                  <span>Tuần {currentWeek.weekNumber} &bull; {getWeekRange(currentWeek)}</span>
                </div>
                <button 
                  className="toolbar-nav-btn" 
                  onClick={() => handleWeekChange('next')}
                  title="Xem tuần sau"
                >
                  <span>Tuần sau</span>
                  <i className="fas fa-chevron-right"></i>
                </button>
              </div>

              <button 
                className="btn-current-week"
                onClick={goToCurrentWeek}
                title="Quay về tuần hiện tại"
              >
                <i className="fas fa-calendar-day" style={{ color: 'var(--duni-primary)' }}></i>
                <span>Tuần hiện tại</span>
              </button>
            </div>

            <div className="toolbar-right">
              {hasChanges && (
                <span className="changes-indicator-pill">
                  <span className="pulsing-dot"></span>
                  Có thay đổi chưa lưu
                </span>
              )}
              <button 
                className="btn-cancel-changes" 
                disabled={!hasChanges}
                onClick={cancelPendingChanges}
                title="Hủy bỏ các thay đổi chưa lưu"
              >
                <i className="fas fa-undo"></i>
                <span>Hủy thay đổi</span>
              </button>
              <button 
                className={`btn-save-changes ${hasChanges ? 'has-changes' : ''}`} 
                disabled={!hasChanges}
                onClick={savePendingChanges}
                title="Lưu tất cả phân công mới"
              >
                <i className="fas fa-save"></i>
                <span>Lưu thay đổi</span>
              </button>
            </div>
          </div>
        )}

        <div className="main-content-wrap">
          {activeTab === 'schedule' ? (
            <div className="schedule-layout">
              {/* Modern Staff Sidebar Panel */}
              <aside className="staff-panel">
                <div className="staff-panel-header">
                  <div className="staff-panel-title">
                    <i className="fas fa-users" style={{ color: 'var(--duni-primary)' }}></i>
                    <span>Danh Sách Nhân Viên</span>
                  </div>
                  <span className="staff-count-badge">{filteredStaff.length} nhân sự</span>
                </div>
                <p className="staff-panel-subtitle">Kéo thả nhân sự vào các ô ca để phân công</p>

                <div className="staff-search-wrapper">
                  <i className="fas fa-search search-icon"></i>
                  <input
                    type="text"
                    value={nameSearch}
                    onChange={(e) => setNameSearch(e.target.value)}
                    placeholder="Tìm tên nhân viên..."
                    className="staff-search-input"
                  />
                  {nameSearch && (
                    <button className="clear-search-btn" onClick={() => setNameSearch('')}>
                      <i className="fas fa-times"></i>
                    </button>
                  )}
                </div>

                <div className="staff-status-filter-wrapper">
                  <span className="role-filter-label">Trạng thái:</span>
                  <div className="staff-select-box">
                    <select
                      value={staffStatusFilter}
                      onChange={(e) => setStaffStatusFilter(e.target.value as 'Hoạt động' | 'Đã nghỉ' | 'all')}
                      className="staff-status-select"
                      aria-label="Lọc trạng thái nhân viên"
                    >
                      <option value="Hoạt động">Hoạt động</option>
                      <option value="Đã nghỉ">Đã nghỉ</option>
                      <option value="all">Tất cả</option>
                    </select>
                    <i className="fas fa-chevron-down select-chevron"></i>
                  </div>
                </div>

                <div className="staff-toggles-section">
                  <label className={`toggle-deleted-label ${showDeletedSchedules ? 'active' : ''}`}>
                    <input
                      type="checkbox"
                      checked={showDeletedSchedules}
                      onChange={(e) => setShowDeletedSchedules(e.target.checked)}
                    />
                    <i className={showDeletedSchedules ? 'fas fa-eye' : 'fas fa-eye-slash'}></i>
                    <span>Hiển thị ca đã xóa</span>
                  </label>
                </div>

                <div className="staff-role-filters">
                  <span className="role-filter-label">Bộ phận:</span>
                  <div className="role-chips-group">
                    {ROLE_OPTIONS.map(role => (
                      <button
                        key={role.value}
                        type="button"
                        className={`role-chip-btn ${selectedRoles.has(role.value) ? 'selected' : ''}`}
                        onClick={() => toggleRoleFilter(role.value)}
                      >
                        {role.label}
                      </button>
                    ))}
                  </div>
                </div>

                <div className="staff-list">
                  {filteredStaff.map(staffMember => (
                    <div 
                      key={staffMember.id}
                      className="staff-item"
                      draggable
                      onDragStart={(e) => handleDragStart(e, staffMember)}
                      style={{ borderLeftColor: staffMember.color || 'var(--duni-primary)' }}
                    >
                      <div className="staff-info">
                        <div className="staff-name-row">
                          <span className="staff-name">{staffMember.name}</span>
                          <span className={`staff-status-badge ${staffMember.status === 'Hoạt động' ? 'status-active' : 'status-inactive'}`}>
                            {staffMember.status}
                          </span>
                        </div>
                        {staffMember.role && (
                          <span className="staff-role-tag">{staffMember.role}</span>
                        )}
                      </div>
                      {canManageEmployees && (
                        <button 
                          className="staff-edit-btn"
                          onClick={(e) => {
                            e.stopPropagation();
                            setEditingStaff(staffMember);
                            setShowAddStaffModal(true);
                          }}
                          title="Chỉnh sửa nhân viên"
                        >
                          <i className="fas fa-pen"></i>
                        </button>
                      )}
                    </div>
                  ))}

                  {filteredStaff.length === 0 && (
                    <div className="staff-empty-state">
                      <i className="fas fa-user-slash"></i>
                      <span>Không tìm thấy nhân viên phù hợp</span>
                    </div>
                  )}
                </div>

                {canManageEmployees && (
                  <div className="staff-panel-footer">
                    <button className="add-staff-outline-btn" onClick={() => {
                      setEditingStaff(null);
                      setShowAddStaffModal(true);
                    }}>
                      <i className="fas fa-plus"></i>
                      <span>Thêm nhân viên mới</span>
                    </button>
                  </div>
                )}

                {error && (
                  <div className="error-message">
                    <span className="error-icon">❌</span>
                    {error}
                  </div>
                )}
              </aside>

              <div className="schedule-grid">
            <div className="mobile-scroll-hint" style={{ display: 'none', padding: '8px 12px', background: '#e0e7ff', color: '#3730a3', fontSize: '13px', borderRadius: '8px', marginBottom: '10px', textAlign: 'center', fontWeight: '600' }}>
              <i className="fas fa-arrows-left-right" style={{ marginRight: '6px' }}></i> Vuốt ngang để xem đầy đủ các ngày trong tuần
            </div>
            <table className="schedule-table">
              <thead>
                <tr>
                  <th style={{ width: '58px' }}>Ca</th>
                  {DAYS.map((day, dayIndex) => {
                    const [year, month, dayOfMonth] = currentWeek.startDate.split('-').map(Number);
                    const date = new Date(year, month - 1, dayOfMonth + dayIndex);
                    const dayNumber = date.getDate();
                    const monthNumber = date.getMonth() + 1;
                    const workDateStr = addDaysToDateString(currentWeek.startDate, dayIndex);
                    const holiday = holidays.find(h => h.holiday_date === workDateStr);
                    return (
                      <th key={day} style={{ position: 'relative', width: 'calc((100% - 58px) / 7)' }}>
                        <div className="day-header-title">{day}</div>
                        <div className="day-header-date-pill">
                          <span>{dayNumber}/{monthNumber}</span>
                          <button
                            disabled={!canManageHolidays}
                            onClick={() => {
                              if (!canManageHolidays) return;
                              setEditingHolidayDate(workDateStr);
                              setEditingHolidayMultiplier(holiday ? holiday.multiplier : 1.5);
                            }}
                            style={{
                              background: 'transparent',
                              border: 'none',
                              color: holiday ? '#ffc107' : 'rgba(255,255,255,0.7)',
                              cursor: 'pointer',
                              fontSize: '11px',
                              padding: '1px',
                              display: 'inline-flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              transition: 'color 0.2s'
                            }}
                            title="Sửa hệ số lương"
                          >
                            <i className="fas fa-edit"></i>
                          </button>
                        </div>
                        {holiday && (
                          <div>
                            <span className="day-holiday-badge">
                              X{holiday.multiplier}
                            </span>
                          </div>
                        )}
                      </th>
                    );
                  })}
                </tr>
              </thead>
              <tbody>
                {SHIFTS.map(shift => (
                  <tr key={shift}>
                    <td className="shift-header-cell">{shift}</td>
                     {DAYS.map((day, dayIndex) => {
                       const workDateStr = addDaysToDateString(currentWeek.startDate, dayIndex);
                       const shiftSchedules = getScheduleForSlot(workDateStr, shift);
                      
                      return (
                        <td 
                          key={`${day}-${shift}`}
                          className="shift-cell drop-zone"
                          onDragOver={handleDragOver}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, workDateStr, shift)}
                        >
                          {shiftSchedules.map(schedule => (
                            <div 
                              key={schedule.id}
                              className="staff-assignment"
                              style={{ 
                                background: schedule.staffColor,
                                color: schedule.staffTextColor,
                                opacity: schedule.isDeleted ? 0.6 : 1,
                                textDecoration: schedule.isDeleted ? 'line-through' : 'none'
                              }}
                            >
                              <span>{schedule.staffName}</span>
                              {schedule.isDeleted ? (
                                <button 
                                  className="restore-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    restoreSchedule(schedule.id);
                                  }}
                                  title="Khôi phục ca làm việc"
                                  style={{
                                    background: '#16a34a',
                                    color: 'white',
                                    border: 'none',
                                    borderRadius: '4px',
                                    padding: '2px 6px',
                                    cursor: 'pointer',
                                    fontSize: '10px',
                                    marginLeft: '4px'
                                  }}
                                >
                                  <i className="fas fa-undo"></i>
                                </button>
                              ) : (
                                <button 
                                  className="assignment-remove-btn"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    removeScheduleFromPending(schedule.id);
                                  }}
                                  title="Xóa ca này"
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              )}
                            </div>
                          ))}
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : (
            <div className="stats-content" style={{ padding: '10px 0 20px' }}>
              {/* Statistics Filter */}
              <div className="bulletin-filter-bar" style={{ marginBottom: '24px' }}>
                <div className="filter-group">
                  <label><i className="far fa-calendar-alt"></i> Từ ngày:</label>
                  <input
                    type="date"
                    value={statsStartDate}
                    onChange={(e) => setStatsStartDate(e.target.value)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '10px',
                      border: '1px solid var(--duni-line)',
                      backgroundColor: 'var(--duni-paper)',
                      color: 'var(--duni-ink)',
                      fontSize: '13px',
                      fontWeight: 600,
                      outline: 'none'
                    }}
                  />
                </div>
                <div className="filter-group">
                  <label><i className="far fa-calendar-alt"></i> Đến ngày:</label>
                  <input
                    type="date"
                    value={statsEndDate}
                    onChange={(e) => setStatsEndDate(e.target.value)}
                    style={{
                      padding: '8px 14px',
                      borderRadius: '10px',
                      border: '1px solid var(--duni-line)',
                      backgroundColor: 'var(--duni-paper)',
                      color: 'var(--duni-ink)',
                      fontSize: '13px',
                      fontWeight: 600,
                      outline: 'none'
                    }}
                  />
                </div>
                <button
                  onClick={loadStatistics}
                  className="btn-primary-action"
                  style={{ marginLeft: 'auto' }}
                >
                  <i className="fas fa-search"></i> Xem thống kê
                </button>
              </div>

              {/* Statistics Display */}
              {scheduleStats && (
                <div className="stats-display" style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%' }}>
                  {/* Top Metric Cards (matching Bulletin Board) */}
                  <div className="bulletin-metric-grid">
                    <div className="bulletin-metric-card">
                      <div className="metric-icon" style={{ background: 'rgba(141, 42, 44, 0.1)', color: 'var(--duni-primary)' }}>
                        <i className="fas fa-calendar-check"></i>
                      </div>
                      <div className="metric-info">
                        <div className="metric-value">{scheduleStats.totalSchedules}</div>
                        <div className="metric-label">Tổng số ca làm việc</div>
                      </div>
                    </div>

                    <div className="bulletin-metric-card">
                      <div className="metric-icon" style={{ background: 'rgba(30, 126, 72, 0.1)', color: '#1e7e48' }}>
                        <i className="fas fa-users"></i>
                      </div>
                      <div className="metric-info">
                        <div className="metric-value">{scheduleStats.uniqueStaff}</div>
                        <div className="metric-label">Số nhân viên làm việc</div>
                      </div>
                    </div>

                    {Object.entries(scheduleStats.schedulesByShift).map(([shift, count]) => (
                      <div key={shift} className="bulletin-metric-card">
                        <div className="metric-icon" style={{ 
                          background: shift === 'Sáng' ? 'rgba(183, 121, 31, 0.12)' : shift === 'Chiều' ? 'rgba(41, 128, 185, 0.12)' : 'rgba(111, 32, 34, 0.12)', 
                          color: shift === 'Sáng' ? '#b7791f' : shift === 'Chiều' ? '#2980b9' : '#6f2022' 
                        }}>
                          <i className={shift === 'Sáng' ? 'fas fa-sun' : shift === 'Chiều' ? 'fas fa-cloud-sun' : 'fas fa-moon'}></i>
                        </div>
                        <div className="metric-info">
                          <div className="metric-value">{count}</div>
                          <div className="metric-label">Ca {shift}</div>
                        </div>
                      </div>
                    ))}
                  </div>

                  {/* Right Column: Statistics by Staff */}
                  <div className="stats-right-column" style={{ width: '100%' }}>
                    <div className="stats-section" style={{ width: '100%' }}>
                      <h3 style={{ marginBottom: '15px', color: '#333' }}>
                        <i className="fas fa-users"></i> Thống kê theo nhân viên
                      </h3>
                      {(() => {
                        // Filter and sort logic
                        let filteredData = scheduleStats.schedulesByStaff.filter(item => {
                          const nameMatch = !nameFilter || item.staff_name.toLowerCase().includes(nameFilter.toLowerCase());
                          const roleMatch = !roleFilter || (item.staff_role || '').toLowerCase().includes(roleFilter.toLowerCase());
                          return nameMatch && roleMatch;
                        });

                        // Sort logic
                        if (sortColumn) {
                          filteredData = [...filteredData].sort((a, b) => {
                            let aVal: any, bVal: any;
                            if (sortColumn === 'staff_name') {
                              aVal = a.staff_name.toLowerCase();
                              bVal = b.staff_name.toLowerCase();
                            } else if (sortColumn === 'staff_role') {
                              aVal = (a.staff_role || '').toLowerCase();
                              bVal = (b.staff_role || '').toLowerCase();
                            } else {
                              aVal = a.count;
                              bVal = b.count;
                            }

                            if (aVal < bVal) return sortDirection === 'asc' ? -1 : 1;
                            if (aVal > bVal) return sortDirection === 'asc' ? 1 : -1;
                            return 0;
                          });
                        }

                        // Get unique roles for filter dropdown
                        const uniqueRoles = Array.from(new Set(scheduleStats.schedulesByStaff.map(item => item.staff_role || '').filter(r => r)));

                        const handleSort = (column: 'staff_name' | 'staff_role' | 'count') => {
                          if (sortColumn === column) {
                            setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
                          } else {
                            setSortColumn(column);
                            setSortDirection('asc');
                          }
                        };

                        return (
                          <div className="staff-stats-table" style={{
                            background: 'white',
                            borderRadius: '8px',
                            overflow: 'hidden',
                            boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                            width: '100%'
                          }}>
                            <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
                              <thead>
                                <tr style={{ background: '#f8f9fa' }}>
                                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', width: '8%' }}>
                                    STT
                                  </th>
                                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', width: '30%' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <span>Tên nhân viên</span>
                                        <button
                                          onClick={() => handleSort('staff_name')}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '2px 4px',
                                            color: sortColumn === 'staff_name' ? '#3498db' : '#666'
                                          }}
                                          title="Sắp xếp"
                                        >
                                          {sortColumn === 'staff_name' ? (
                                            sortDirection === 'asc' ? '↑' : '↓'
                                          ) : (
                                            '⇅'
                                          )}
                                        </button>
                                      </div>
                                      <input
                                        type="text"
                                        placeholder="Lọc tên..."
                                        value={nameFilter}
                                        onChange={(e) => setNameFilter(e.target.value)}
                                        style={{
                                          width: '100%',
                                          padding: '4px 8px',
                                          border: '1px solid #ddd',
                                          borderRadius: '4px',
                                          fontSize: '12px'
                                        }}
                                      />
                                    </div>
                                  </th>
                                  <th style={{ padding: '8px', textAlign: 'left', borderBottom: '2px solid #dee2e6', width: '30%' }}>
                                    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
                                      <div style={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                                        <span>Vai trò</span>
                                        <button
                                          onClick={() => handleSort('staff_role')}
                                          style={{
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            padding: '2px 4px',
                                            color: sortColumn === 'staff_role' ? '#3498db' : '#666'
                                          }}
                                          title="Sắp xếp"
                                        >
                                          {sortColumn === 'staff_role' ? (
                                            sortDirection === 'asc' ? '↑' : '↓'
                                          ) : (
                                            '⇅'
                                          )}
                                        </button>
                                      </div>
                                      <select
                                        value={roleFilter}
                                        onChange={(e) => setRoleFilter(e.target.value)}
                                        style={{
                                          width: '100%',
                                          padding: '4px 8px',
                                          border: '1px solid #ddd',
                                          borderRadius: '4px',
                                          fontSize: '12px',
                                          background: 'white'
                                        }}
                                      >
                                        <option value="">Tất cả vai trò</option>
                                        {uniqueRoles.map(role => (
                                          <option key={role} value={role}>{role}</option>
                                        ))}
                                      </select>
                                    </div>
                                  </th>
                                  <th style={{ padding: '8px', textAlign: 'right', borderBottom: '2px solid #dee2e6', width: '32%' }}>
                                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '4px' }}>
                                      <span>Số ca làm việc</span>
                                      <button
                                        onClick={() => handleSort('count')}
                                        style={{
                                          background: 'none',
                                          border: 'none',
                                          cursor: 'pointer',
                                          padding: '2px 4px',
                                          color: sortColumn === 'count' ? '#3498db' : '#666'
                                        }}
                                        title="Sắp xếp"
                                      >
                                        {sortColumn === 'count' ? (
                                          sortDirection === 'asc' ? '↑' : '↓'
                                        ) : (
                                          '⇅'
                                        )}
                                      </button>
                                    </div>
                                  </th>
                                </tr>
                              </thead>
                              <tbody>
                                {filteredData.length > 0 ? (
                                  filteredData.map((item, index) => (
                                    <tr key={`${item.staff_name}-${index}`} style={{ borderBottom: '1px solid #dee2e6' }}>
                                      <td style={{ padding: '12px' }}>{index + 1}</td>
                                      <td style={{ padding: '12px', fontWeight: '500' }}>
                                        {item.staff_name}
                                      </td>
                                      <td style={{ padding: '12px', fontWeight: '500' }}>
                                        {item.staff_role || '-'}
                                      </td>
                                      <td style={{ padding: '12px', textAlign: 'right', fontWeight: 'bold', color: '#3498db' }}>
                                        {item.count}
                                      </td>
                                    </tr>
                                  ))
                                ) : (
                                  <tr>
                                    <td colSpan={4} style={{ padding: '20px', textAlign: 'center', color: '#999' }}>
                                      {scheduleStats.schedulesByStaff.length === 0 
                                        ? 'Không có dữ liệu trong khoảng thời gian này'
                                        : 'Không tìm thấy kết quả phù hợp với bộ lọc'
                                      }
                                    </td>
                                  </tr>
                                )}
                              </tbody>
                            </table>
                          </div>
                        );
                      })()}
                    </div>
                  </div>
                </div>
              )}

              {!scheduleStats && (
                <div style={{ 
                  textAlign: 'center', 
                  padding: '40px', 
                  color: '#999',
                  background: '#f5f5f5',
                  borderRadius: '8px'
                }}>
                  <i className="fas fa-chart-bar" style={{ fontSize: '48px', marginBottom: '15px', opacity: 0.3 }}></i>
                  <div>Chọn khoảng thời gian và nhấn "Xem thống kê" để xem dữ liệu</div>
                </div>
              )}

              {error && (
                <div className="error-message" style={{
                  marginTop: '20px',
                  padding: '15px',
                  background: '#fee',
                  color: '#c33',
                  borderRadius: '4px',
                  border: '1px solid #fcc'
                }}>
                  <span className="error-icon">❌</span>
                  {error}
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Comprehensive Staff Modal (Add & Edit) */}
      <StaffFormModal
        isOpen={showAddStaffModal}
        onClose={() => {
          setShowAddStaffModal(false);
          setEditingStaff(null);
        }}
        staff={editingStaff}
        onSave={handleSaveStaff}
        onDelete={deleteStaff}
        banks={banks}
      />

      {/* Holiday edit modal */}
      {editingHolidayDate && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'rgba(0, 0, 0, 0.4)',
          backdropFilter: 'blur(4px)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          zIndex: 2000
        }}>
          <div style={{
            background: 'white',
            padding: '25px',
            borderRadius: '12px',
            boxShadow: '0 10px 25px rgba(0,0,0,0.15)',
            width: '320px',
            border: '1px solid rgba(255, 255, 255, 0.2)'
          }}>
            <h3 style={{ marginBottom: '15px', color: '#1e293b', fontSize: '18px', fontWeight: '600' }}>
              Thiết lập Hệ số Lương
            </h3>
            <p style={{ fontSize: '14px', color: '#64748b', marginBottom: '15px' }}>
              Ngày: <strong>{(() => {
                const [year, month, day] = editingHolidayDate.split('-');
                return `${day}/${month}/${year}`;
              })()}</strong>
            </p>
            
            <div style={{ marginBottom: '20px' }}>
              <label style={{ display: 'block', marginBottom: '8px', fontSize: '13px', fontWeight: '500', color: '#475569' }}>
                Chọn hệ số nhân lương:
              </label>
              <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                <select
                  value={editingHolidayMultiplier}
                  onChange={(e) => setEditingHolidayMultiplier(parseFloat(e.target.value))}
                  style={{
                    flex: 1,
                    padding: '8px 12px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    fontSize: '14px',
                    background: 'white',
                    color: '#333'
                  }}
                >
                  <option value={1.0}>1.0 (Không có lễ)</option>
                  <option value={1.5}>1.5</option>
                  <option value={2.0}>2.0</option>
                  <option value={2.5}>2.5</option>
                  <option value={3.0}>3.0</option>
                  <option value={3.5}>3.5</option>
                </select>
                <input
                  type="number"
                  step="0.1"
                  min="1"
                  max="10"
                  value={editingHolidayMultiplier}
                  onChange={(e) => setEditingHolidayMultiplier(parseFloat(e.target.value) || 1.0)}
                  style={{
                    width: '65px',
                    padding: '8px',
                    border: '1px solid #cbd5e1',
                    borderRadius: '6px',
                    fontSize: '14px',
                    textAlign: 'center',
                    color: '#333'
                  }}
                />
              </div>
            </div>

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button
                onClick={() => setEditingHolidayDate(null)}
                style={{
                  padding: '8px 16px',
                  background: '#f1f5f9',
                  color: '#475569',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '500'
                }}
              >
                Hủy
              </button>
              <button
                onClick={handleSaveHolidayMultiplier}
                style={{
                  padding: '8px 16px',
                  background: 'var(--duni-primary)',
                  color: 'white',
                  border: 'none',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  fontSize: '13px',
                  fontWeight: '600'
                }}
              >
                Lưu
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
